import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import PrintFooter from '../../components/print/PrintFooter';
import PagedPrintView from '../../components/print/PagedPrintView';
import PaymentDetails from '../../components/print/PaymentDetails';
import { useVoidPasscode } from '../../components/common/VoidPasscodeModal';

export default function QuotationPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const [quot, setQuot] = useState(null);
  const [terms, setTerms] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const { prompt: promptVoidPin, VoidPasscodeModal } = useVoidPasscode();

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!loading && quot && searchParams.get('print') === '1') {
      if (document.readyState === 'complete') {
        setTimeout(() => window.print(), 300);
      } else {
        window.addEventListener('load', () => window.print(), { once: true });
      }
    }
  }, [loading, quot, searchParams]);

  async function load() {
    const { data } = await supabase.from('quotations')
      .select('*, clients(full_name, client_code, email, phone, address, agents:care_of_agent_id(tls_code, full_name)), currency:currency_id(code)')
      .eq('id', id).single();
    setQuot(data);
    if (data?.item_type) {
      const { data: t } = await supabase.from('terms_templates').select('content').eq('product_type', data.item_type).single();
      setTerms(t?.content || '');
    }
    setLoading(false);
  }

  async function markSent() {
    setUpdating(true);
    await supabase.from('quotations').update({ status: 'SENT', updated_by: username }).eq('id', id);
    setQuot(q => ({ ...q, status: 'SENT' }));
    setUpdating(false);
  }

  async function voidQuotation() {
    let staffName;
    try { staffName = await promptVoidPin(); } catch { return; }
    setUpdating(true);
    const now = new Date().toISOString();
    await supabase.from('quotations').update({ status: 'VOID', voided_by: staffName, voided_at: now, updated_by: username }).eq('id', id);
    setQuot(q => ({ ...q, status: 'VOID', voided_by: staffName, voided_at: now }));
    setUpdating(false);
  }

  async function reinstateQuotation() {
    setUpdating(true);
    await supabase.from('quotations').update({ status: 'DRAFT', voided_by: null, voided_at: null, updated_by: username }).eq('id', id);
    setQuot(q => ({ ...q, status: 'DRAFT', voided_by: null, voided_at: null }));
    setUpdating(false);
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading…</div>;
  if (!quot) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Quotation not found.</div>;

  const client = quot.clients;
  const op = quot.op_data || {};
  const total = parseFloat(quot.total_amount || 0);
  const STATUS_COLORS = { DRAFT: '#888', SENT: '#4f6ef7', CONVERTED: '#16a34a' };

  // Build line item from quotation data
  const lineItem = buildLineItem(quot);

  return (
    <>
      {VoidPasscodeModal}
      {/* Action bar */}
      <div className="no-print" style={{
        display: 'flex', gap: 10, alignItems: 'center', padding: '12px 24px',
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button className="btn btn-secondary" onClick={() => navigate('/quotations')}>← Back</button>
        <div style={{ flex: 1 }} />
        {quot.status !== 'VOID' && (
          <button className="btn btn-secondary" onClick={() => navigate(`/quotations/${id}/edit`)} disabled={updating}>Edit</button>
        )}
        {quot.converted_invoice_id && (
          <button className="btn btn-secondary" onClick={() => navigate(`/invoices/${quot.converted_invoice_id}`)}>
            View Invoice →
          </button>
        )}
        {quot.status !== 'VOID' && quot.status !== 'CONVERTED' && (
          <button className="btn btn-danger" onClick={voidQuotation} disabled={updating}>Void</button>
        )}
        {quot.status === 'VOID' && (
          <button className="btn btn-secondary" onClick={reinstateQuotation} disabled={updating}>Reinstate</button>
        )}
        <button className="btn btn-primary" onClick={() => window.print()}>Print / PDF</button>
      </div>

      <PagedPrintView docKey={id}>
        {/* PagedJS only starts repeating a "running" element (like the footer) on
            pages AFTER it first encounters that element in the source content — so
            it must come first here, even though position:running() means it never
            renders in place. */}
        <PrintFooter />

        {quot.status === 'VOID' && (
          <>
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none', zIndex: 10,
            }}>
              <div style={{
                fontSize: 96, fontWeight: 900, color: 'rgba(239,68,68,0.12)',
                transform: 'rotate(-30deg)', letterSpacing: 8, userSelect: 'none',
              }}>VOID</div>
            </div>
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 14px', marginBottom: 20, fontSize: 12, color: '#dc2626', display: 'flex', gap: 16 }}>
              <span style={{ fontWeight: 700 }}>VOID</span>
              {quot.voided_by && <span>Cancelled by: <strong>{quot.voided_by}</strong></span>}
              {quot.voided_at && <span>{new Date(quot.voided_at).toLocaleString()}</span>}
            </div>
          </>
        )}
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 36 }}>
          <div>
            <img src="/travelscape-logo-full.png" alt="Travelscape" style={{ height: 83, display: 'block', marginBottom: -14, marginLeft: -9 }} />
            <img src="/iata-logo.svg" alt="IATA" style={{ width: 92, height: 'auto', display: 'block', marginBottom: 2 }} />
            <div style={{ fontSize: 11, color: '#555', lineHeight: 1.7 }}>
              Travelscape Lanka Pvt Ltd<br />
              29/3 Colonel T.G Jayawardena Mawatha<br />
              Colombo 03, Sri Lanka
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#f07040', letterSpacing: -0.5 }}>PRO FORMA INVOICE</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{quot.quotation_number}</div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>
              <div>Issued: <strong>{quot.created_at?.slice(0, 10)}</strong></div>
              {quot.valid_until && <div>Valid Until: <strong>{quot.valid_until}</strong></div>}
            </div>
            {client?.agents?.tls_code && (
              <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: '#f07040', letterSpacing: 0.5 }}>{client.agents.tls_code}</div>
            )}
          </div>
        </div>

        <div style={{ borderTop: '2px solid #f07040', marginBottom: 28 }} />

        {/* Client */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Prepared For</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{client?.full_name || '—'}</div>
          {client?.phone && <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{client.phone}</div>}
          {client?.email && <div style={{ fontSize: 12, color: '#555' }}>{client.email}</div>}
          {client?.address && <div style={{ fontSize: 12, color: '#555', whiteSpace: 'pre-line' }}>{client.address}</div>}
        </div>

        {/* Line items */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
          <thead>
            <tr style={{ background: '#fff4ef' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#f07040', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description</th>
              <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#f07040', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pax</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#f07040', textTransform: 'uppercase', letterSpacing: 0.5 }}>Per Pax</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#f07040', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(quot.op_data?.line_items?.length ? quot.op_data.line_items : [{ type: 'Adult', unitPrice: quot.amount_per_pax || total / (quot.num_pax || 1), qty: quot.num_pax || 1, amount: total }]).map((li, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ fontWeight: 500 }}>{li.type} — {lineItem.description}</div>
                  {i === 0 && lineItem.sub && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{lineItem.sub}</div>}
                </td>
                <td style={{ textAlign: 'center', padding: '10px 12px' }}>{li.qty}</td>
                <td style={{ textAlign: 'right', padding: '10px 12px' }}>{quot.currency?.code} {fmt(li.unitPrice)}</td>
                <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 500 }}>{quot.currency?.code} {fmt(li.amount)}</td>
              </tr>
            ))}
            {quot.details && (
              <tr>
                <td colSpan={4} style={{ padding: '8px 12px', fontSize: 12, color: '#555', background: '#fafafa', whiteSpace: 'pre-line' }}>
                  <strong style={{ color: '#888', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Inclusions: </strong>
                  {quot.details}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Total */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 32 }}>
          <div style={{ width: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f07040', color: 'white', borderRadius: 6, fontWeight: 700, fontSize: 15 }}>
              <span>Total</span>
              <span>{quot.currency?.code} {fmt(total)}</span>
            </div>
          </div>
        </div>

        {/* Notes — free text can run longer than one page, so only the heading is
            protected; forcing the whole block unbreakable makes it overflow the
            margin into the footer instead of paginating normally. */}
        {quot.notes && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: 16, marginBottom: 16 }}>
            <div className="avoid-break" style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Notes</div>
            <div style={{ fontSize: 12, color: '#555', whiteSpace: 'pre-line' }}>{quot.notes}</div>
          </div>
        )}

        {terms && (
          <div style={{ marginTop: 32, borderTop: '2px solid #f07040', paddingTop: 20 }}>
            <div className="avoid-break" style={{ fontSize: 12, fontWeight: 800, color: '#f07040', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 }}>
              Terms &amp; Conditions
            </div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10.5, color: '#444', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
              {terms}
            </div>
          </div>
        )}

        {/* Payment Details — kept on its own page so the running footer never overlaps it */}
        <div className="avoid-break" style={{ breakBefore: 'page', pageBreakBefore: 'always' }}>
          <PaymentDetails />
          <div style={{ borderTop: '1px solid #eee', paddingTop: 14, marginTop: 24, marginBottom: 16, textAlign: 'center', fontSize: 11, color: '#aaa' }}>
            This pro forma is valid until {quot.valid_until || 'further notice'}.
          </div>
        </div>
      </PagedPrintView>
    </>
  );
}

function buildLineItem(quot) {
  const op = quot.op_data || {};
  if (quot.item_type === 'ticket') {
    const sub = [
      op.airline ? `Airline: ${op.airline}` : null,
      op.pnr ? `PNR: ${op.pnr}` : null,
      op.route ? `Route: ${op.route}` : null,
      op.travel_date ? `Travel: ${op.travel_date}` : null,
    ].filter(Boolean).join(' · ');
    return { description: `Flight Ticket${op.airline ? ` — ${op.airline}` : ''}`, sub };
  }
  if (quot.item_type === 'visa') {
    const sub = [op.country, op.visa_type].filter(Boolean).join(' — ');
    return { description: `Visa Processing${op.country ? ` — ${op.country}` : ''}`, sub };
  }
  if (quot.item_type === 'package') {
    return { description: 'Travel Package', sub: quot.details ? null : null };
  }
  return { description: 'Service', sub: null };
}

function fmt(n) {
  if (n == null || isNaN(n)) return '0.00';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
